import Paper from '@/components/Paper'
import { CHART_COLORS } from '@/constants/chartColors'
import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Line,
    Bar,
    BarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Area,
    AreaChart,
} from 'recharts'
import { Loading, Typogaphy, Checkbox } from 'vnetwork-ui'
import { getRevenueCashFlow } from '../../../services'
import { SalesTargetFilters } from '../../../types'
import { PDFExportButton, ExcelExportButton } from '../ExportButtons'
import {
    Banknote,
    TrendingUp,
    TrendingDown,
    Minus,
    Users,
    FileText,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react'

interface RevenueCashFlowProps {
    filters: SalesTargetFilters
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50]

const RevenueCashFlow = ({ filters }: RevenueCashFlowProps) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const [showComparison, setShowComparison] = useState(false)

    // Pagination state for employee table
    const [employeePage, setEmployeePage] = useState(1)
    const [employeePageSize, setEmployeePageSize] = useState(10)

    // Calculate previous year date range
    const previousYearFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null
        return {
            gte: dayjs(filters.gte).subtract(1, 'year').format('YYYY-MM-DD'),
            lte: dayjs(filters.lte).subtract(1, 'year').format('YYYY-MM-DD'),
        }
    }, [filters.gte, filters.lte])

    const { data, renderState, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'revenue-cash-flow',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getRevenueCashFlow({
                gte: filters.gte,
                lte: filters.lte,
                employee_ids: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Fetch previous year data for comparison
    const { data: previousYearData, isLoading: isPrevYearLoading } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
                'revenue-cash-flow-prev-year',
                previousYearFilters?.gte,
                previousYearFilters?.lte,
                filters?.employee_id ?? [],
            ],
            queryFn: () =>
                getRevenueCashFlow({
                    gte: previousYearFilters?.gte,
                    lte: previousYearFilters?.lte,
                    employee_ids: filters?.employee_id ?? [],
                }),
            enabled:
                showComparison &&
                !!previousYearFilters?.gte &&
                !!previousYearFilters?.lte,
        })

    // Helper function to calculate growth rate
    const calculateGrowthRate = (
        current: number,
        previous: number | undefined
    ): number | null => {
        if (previous === undefined || previous === 0) return null
        return ((current - previous) / previous) * 100
    }

    // Process previous year data
    const previousYearMonthlyMap = useMemo(() => {
        if (
            !showComparison ||
            !previousYearData ||
            !Array.isArray(previousYearData) ||
            previousYearData.length === 0
        )
            return new Map()

        const monthlyMap = new Map<
            string,
            {
                total: number
                renewal: number
                newSale: number
                upsell: number
                count: number
            }
        >()

        previousYearData.forEach((employee) => {
            employee.monthly_breakdown?.forEach((month) => {
                // Convert to MM format for matching (ignore year)
                const monthKey = dayjs(month.payment_month).format('MM')
                const existing = monthlyMap.get(monthKey) || {
                    total: 0,
                    renewal: 0,
                    newSale: 0,
                    upsell: 0,
                    count: 0,
                }
                monthlyMap.set(monthKey, {
                    total: existing.total + (month.total_revenue || 0),
                    renewal: existing.renewal + (month.renewal_revenue || 0),
                    newSale: existing.newSale + (month.new_sale_revenue || 0),
                    upsell: existing.upsell + (month.upsell_revenue || 0),
                    count: existing.count + (month.allocation_count || 0),
                })
            })
        })

        return monthlyMap
    }, [showComparison, previousYearData])

    // Process data for charts
    const chartData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) return []

        // Aggregate all monthly breakdowns across employees
        const monthlyMap = new Map<
            string,
            {
                total: number
                renewal: number
                newSale: number
                upsell: number
                count: number
            }
        >()

        data.forEach((employee) => {
            employee.monthly_breakdown?.forEach((month) => {
                const existing = monthlyMap.get(month.payment_month) || {
                    total: 0,
                    renewal: 0,
                    newSale: 0,
                    upsell: 0,
                    count: 0,
                }
                monthlyMap.set(month.payment_month, {
                    total: existing.total + (month.total_revenue || 0),
                    renewal: existing.renewal + (month.renewal_revenue || 0),
                    newSale: existing.newSale + (month.new_sale_revenue || 0),
                    upsell: existing.upsell + (month.upsell_revenue || 0),
                    count: existing.count + (month.allocation_count || 0),
                })
            })
        })

        // Sort by month and convert to array with comparison data
        return Array.from(monthlyMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, values]) => {
                const monthKey = dayjs(month).format('MM')
                const prevData = previousYearMonthlyMap.get(monthKey)

                return {
                    month: dayjs(month).format('MM/YYYY'),
                    rawMonth: month,
                    totalRevenue: values.total,
                    renewal: values.renewal,
                    newSale: values.newSale,
                    upsell: values.upsell,
                    count: values.count,
                    // Previous year data
                    prevTotalRevenue: prevData?.total || 0,
                    prevRenewal: prevData?.renewal || 0,
                    prevNewSale: prevData?.newSale || 0,
                    prevUpsell: prevData?.upsell || 0,
                    prevCount: prevData?.count || 0,
                    // Growth rates
                    totalGrowth: calculateGrowthRate(
                        values.total,
                        prevData?.total
                    ),
                    renewalGrowth: calculateGrowthRate(
                        values.renewal,
                        prevData?.renewal
                    ),
                    newSaleGrowth: calculateGrowthRate(
                        values.newSale,
                        prevData?.newSale
                    ),
                    upsellGrowth: calculateGrowthRate(
                        values.upsell,
                        prevData?.upsell
                    ),
                }
            })
    }, [data, previousYearMonthlyMap])

    // Summary statistics
    const summary = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return {
                totalRevenue: 0,
                renewalRevenue: 0,
                newSaleRevenue: 0,
                upsellRevenue: 0,
                totalAllocations: 0,
                employeeCount: 0,
            }
        }

        return {
            totalRevenue: data.reduce(
                (sum, e) => sum + (e.total_revenue || 0),
                0
            ),
            renewalRevenue: data.reduce(
                (sum, e) => sum + (e.renewal_revenue || 0),
                0
            ),
            newSaleRevenue: data.reduce(
                (sum, e) => sum + (e.new_sale_revenue || 0),
                0
            ),
            upsellRevenue: data.reduce(
                (sum, e) => sum + (e.upsell_revenue || 0),
                0
            ),
            totalAllocations: data.reduce(
                (sum, e) => sum + (e.allocation_count || 0),
                0
            ),
            employeeCount: data.length,
        }
    }, [data])

    // Get unique months from chartData for employee table columns
    const uniqueMonths = useMemo(() => {
        return chartData.map((row) => ({
            key: row.rawMonth,
            label: row.month,
        }))
    }, [chartData])

    // Employee breakdown data for table
    const employeeBreakdownData = useMemo(() => {
        if (!data || !Array.isArray(data) || data.length === 0) return []

        return data
            .map((employee) => {
                // Create a map of monthly data for this employee
                const monthlyMap = new Map<
                    string,
                    {
                        total: number
                        renewal: number
                        newSale: number
                        upsell: number
                    }
                >()

                employee.monthly_breakdown?.forEach((month) => {
                    monthlyMap.set(month.payment_month, {
                        total: month.total_revenue || 0,
                        renewal: month.renewal_revenue || 0,
                        newSale: month.new_sale_revenue || 0,
                        upsell: month.upsell_revenue || 0,
                    })
                })

                return {
                    employee_id: employee.employee_id,
                    employee_name: employee.employee_name,
                    total_revenue: employee.total_revenue || 0,
                    renewal_revenue: employee.renewal_revenue || 0,
                    new_sale_revenue: employee.new_sale_revenue || 0,
                    upsell_revenue: employee.upsell_revenue || 0,
                    allocation_count: employee.allocation_count || 0,
                    monthlyData: monthlyMap,
                }
            })
            .sort((a, b) => b.total_revenue - a.total_revenue) // Sort by total revenue descending
    }, [data])

    // Paginated employee data
    const paginatedEmployeeData = useMemo(() => {
        const startIndex = (employeePage - 1) * employeePageSize
        const endIndex = startIndex + employeePageSize
        return employeeBreakdownData.slice(startIndex, endIndex)
    }, [employeeBreakdownData, employeePage, employeePageSize])

    // Total pages for employee table
    const totalEmployeePages = useMemo(() => {
        return Math.ceil(employeeBreakdownData.length / employeePageSize)
    }, [employeeBreakdownData.length, employeePageSize])

    // Reset page when data changes
    useEffect(() => {
        setEmployeePage(1)
    }, [data])

    const formatYAxis = (value: number) => {
        if (value >= 1_000_000_000) {
            return `${(value / 1_000_000_000).toFixed(1)}B`
        } else if (value >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(1)}M`
        } else {
            return `${(value / 1_000).toFixed(0)}K`
        }
    }

    // Helper to render growth cell
    const renderGrowthCell = (growth: number | null) => {
        if (growth === null) return <span className="text-gray-400">-</span>

        if (growth > 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-green-600">
                    <TrendingUp size={12} />
                    <span className="text-xs font-medium">
                        +{growth.toFixed(1)}%
                    </span>
                </div>
            )
        } else if (growth < 0) {
            return (
                <div className="flex items-center justify-end gap-0.5 text-red-600">
                    <TrendingDown size={12} />
                    <span className="text-xs font-medium">
                        {growth.toFixed(1)}%
                    </span>
                </div>
            )
        }
        return (
            <div className="flex items-center justify-end gap-0.5 text-gray-500">
                <Minus size={12} />
                <span className="text-xs font-medium">0%</span>
            </div>
        )
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            // Group current and previous year data
            const currentData = payload.filter(
                (entry: any) => !entry.dataKey.startsWith('prev')
            )
            const prevData = payload.filter((entry: any) =>
                entry.dataKey.startsWith('prev')
            )

            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg max-w-xs">
                    <p className="font-medium mb-2">{label}</p>
                    {currentData.map((entry: any, index: number) => (
                        <p
                            key={index}
                            className="text-sm"
                            style={{ color: entry.color }}
                        >
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                    {showComparison && prevData.length > 0 && (
                        <>
                            <hr className="my-2 border-gray-200" />
                            <p className="text-xs text-gray-500 mb-1">
                                {t('reports.services.same_period_last_year')}
                            </p>
                            {prevData.map((entry: any, index: number) => (
                                <p
                                    key={index}
                                    className="text-sm opacity-70"
                                    style={{ color: entry.color }}
                                >
                                    {entry.name}: {formatCurrency(entry.value)}
                                </p>
                            ))}
                        </>
                    )}
                </div>
            )
        }
        return null
    }

    // Prepare export data
    const exportData = useMemo(() => {
        return chartData.map((row) => ({
            [t('common.month')]: row.month,
            [t('revenue_cash_flow.total_revenue')]: row.totalRevenue,
            [t('revenue_cash_flow.renewal')]: row.renewal,
            [t('revenue_cash_flow.new_sale')]: row.newSale,
            [t('revenue_cash_flow.upsell')]: row.upsell,
            [t('revenue_cash_flow.allocation_count')]: row.count,
        }))
    }, [chartData, t])

    // KPI Cards
    const kpiCards = [
        {
            label: t('revenue_cash_flow.total_cash_collected'),
            value: summary.totalRevenue,
            icon: Banknote,
            color: 'bg-blue-50 text-blue-600',
            iconBg: 'bg-blue-100',
            format: true,
        },
        {
            label: t('revenue_cash_flow.renewal_revenue'),
            value: summary.renewalRevenue,
            icon: TrendingUp,
            color: 'bg-green-50 text-green-600',
            iconBg: 'bg-green-100',
            format: true,
        },
        {
            label: t('revenue_cash_flow.new_sale_revenue'),
            value: summary.newSaleRevenue,
            icon: TrendingUp,
            color: 'bg-purple-50 text-purple-600',
            iconBg: 'bg-purple-100',
            format: true,
        },
        {
            label: t('revenue_cash_flow.upsell_revenue'),
            value: summary.upsellRevenue,
            icon: TrendingUp,
            color: 'bg-amber-50 text-amber-600',
            iconBg: 'bg-amber-100',
            format: true,
        },
        {
            label: t('revenue_cash_flow.total_invoices'),
            value: summary.totalAllocations,
            icon: FileText,
            color: 'bg-gray-50 text-gray-600',
            iconBg: 'bg-gray-100',
            format: false,
        },
        {
            label: t('revenue_cash_flow.employees'),
            value: summary.employeeCount,
            icon: Users,
            color: 'bg-cyan-50 text-cyan-600',
            iconBg: 'bg-cyan-100',
            format: false,
        },
    ]

    if (isLoading) {
        return (
            <Paper className="flex justify-center items-center h-[400px]">
                <Loading.CircularProgress width={32} height={32} />
            </Paper>
        )
    }

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    return (
        <div className="space-y-2">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {kpiCards.map((card) => (
                    <div
                        key={card.label}
                        className={`${card.color} rounded-lg p-4 border border-gray-100 shadow-sm`}
                    >
                        <div className="flex items-center gap-3">
                            <div className={`${card.iconBg} p-2 rounded-lg`}>
                                <card.icon size={20} />
                            </div>
                            <div className="min-w-0">
                                <Typogaphy className="text-lg font-bold truncate">
                                    {card.format
                                        ? formatCurrency(card.value)
                                        : card.value.toLocaleString()}
                                </Typogaphy>
                                <Typogaphy className="text-xs text-gray-600 truncate">
                                    {card.label}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Chart */}
            <Paper className="space-y-3">
                <div className="flex items-center justify-between">
                    <Typogaphy size="medium" className="font-medium">
                        {t('revenue_cash_flow.monthly_cash_flow_trend')}
                    </Typogaphy>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={showComparison}
                                onChange={() =>
                                    setShowComparison(!showComparison)
                                }
                                size="small"
                                label={t(
                                    'reports.services.compare_with_last_year'
                                )}
                            />
                        </div>
                        <div className="text-sm text-gray-500">
                            {t('revenue_cash_flow.based_on_payment_date')}
                        </div>
                    </div>
                </div>
                {showComparison && isPrevYearLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Loading.CircularProgress width={16} height={16} />
                        <span>{t('common.loading')}</span>
                    </div>
                )}

                <div ref={chartRef} className="relative">
                    <div className="absolute top-0 right-0 flex gap-2">
                        <PDFExportButton
                            chartRef={chartRef}
                            filename="revenue-cash-flow"
                        />
                    </div>
                    <ResponsiveContainer width="100%" height={350}>
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient
                                    id="colorTotal"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={CHART_COLORS.PRIMARY_BLUE}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={CHART_COLORS.PRIMARY_BLUE}
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={SCHEMES.gray}
                            />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis
                                tickFormatter={formatYAxis}
                                tick={{ fontSize: 12 }}
                                width={60}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="totalRevenue"
                                name={t('revenue_cash_flow.total_revenue')}
                                stroke={CHART_COLORS.PRIMARY_BLUE}
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorTotal)"
                            />
                            <Line
                                type="monotone"
                                dataKey="renewal"
                                name={t('revenue_cash_flow.renewal')}
                                stroke={CHART_COLORS.EMERALD_GREEN}
                                strokeWidth={2}
                                dot={{ fill: CHART_COLORS.EMERALD_GREEN, r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="newSale"
                                name={t('revenue_cash_flow.new_sale')}
                                stroke={CHART_COLORS.INDIGO_PURPLE}
                                strokeWidth={2}
                                dot={{ fill: CHART_COLORS.INDIGO_PURPLE, r: 3 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="upsell"
                                name={t('revenue_cash_flow.upsell')}
                                stroke={CHART_COLORS.AMBER_ORANGE}
                                strokeWidth={2}
                                dot={{ fill: CHART_COLORS.AMBER_ORANGE, r: 3 }}
                            />
                            {/* Previous year comparison lines (dashed) */}
                            {showComparison && (
                                <>
                                    <Line
                                        type="monotone"
                                        dataKey="prevTotalRevenue"
                                        name={`${t('revenue_cash_flow.total_revenue')} (${t('common.last_year')})`}
                                        stroke={CHART_COLORS.PRIMARY_BLUE}
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        opacity={0.5}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="prevRenewal"
                                        name={`${t('revenue_cash_flow.renewal')} (${t('common.last_year')})`}
                                        stroke={CHART_COLORS.EMERALD_GREEN}
                                        strokeWidth={1.5}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        opacity={0.5}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="prevNewSale"
                                        name={`${t('revenue_cash_flow.new_sale')} (${t('common.last_year')})`}
                                        stroke={CHART_COLORS.INDIGO_PURPLE}
                                        strokeWidth={1.5}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        opacity={0.5}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="prevUpsell"
                                        name={`${t('revenue_cash_flow.upsell')} (${t('common.last_year')})`}
                                        stroke={CHART_COLORS.AMBER_ORANGE}
                                        strokeWidth={1.5}
                                        strokeDasharray="5 5"
                                        dot={false}
                                        opacity={0.5}
                                    />
                                </>
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                    {/* Custom Legend */}
                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-2">
                        {/* Total Revenue */}
                        <div className="flex items-center gap-1.5">
                            <svg width="20" height="10">
                                <line
                                    x1="0"
                                    y1="5"
                                    x2="20"
                                    y2="5"
                                    stroke={CHART_COLORS.PRIMARY_BLUE}
                                    strokeWidth="3"
                                />
                            </svg>
                            <span className="text-xs font-medium text-blue-600">
                                {t('revenue_cash_flow.total_revenue')}
                            </span>
                        </div>
                        {showComparison && (
                            <div className="flex items-center gap-1.5">
                                <svg width="20" height="10">
                                    <line
                                        x1="0"
                                        y1="5"
                                        x2="20"
                                        y2="5"
                                        stroke={CHART_COLORS.PRIMARY_BLUE}
                                        strokeWidth="2"
                                        strokeDasharray="4 2"
                                        opacity="0.5"
                                    />
                                </svg>
                                <span className="text-xs text-blue-400">
                                    {t('revenue_cash_flow.total_revenue')}{' '}
                                    {dayjs(filters.gte)
                                        .subtract(1, 'year')
                                        .format('YYYY')}
                                </span>
                            </div>
                        )}
                        {/* Renewal */}
                        <div className="flex items-center gap-1.5">
                            <svg width="20" height="10">
                                <line
                                    x1="0"
                                    y1="5"
                                    x2="20"
                                    y2="5"
                                    stroke={CHART_COLORS.EMERALD_GREEN}
                                    strokeWidth="2"
                                />
                            </svg>
                            <span className="text-xs font-medium text-green-600">
                                {t('revenue_cash_flow.renewal')}
                            </span>
                        </div>
                        {showComparison && (
                            <div className="flex items-center gap-1.5">
                                <svg width="20" height="10">
                                    <line
                                        x1="0"
                                        y1="5"
                                        x2="20"
                                        y2="5"
                                        stroke={CHART_COLORS.EMERALD_GREEN}
                                        strokeWidth="2"
                                        strokeDasharray="4 2"
                                        opacity="0.5"
                                    />
                                </svg>
                                <span className="text-xs text-green-400">
                                    {t('revenue_cash_flow.renewal')}{' '}
                                    {dayjs(filters.gte)
                                        .subtract(1, 'year')
                                        .format('YYYY')}
                                </span>
                            </div>
                        )}
                        {/* New Sale */}
                        <div className="flex items-center gap-1.5">
                            <svg width="20" height="10">
                                <line
                                    x1="0"
                                    y1="5"
                                    x2="20"
                                    y2="5"
                                    stroke={CHART_COLORS.INDIGO_PURPLE}
                                    strokeWidth="2"
                                />
                            </svg>
                            <span className="text-xs font-medium text-purple-600">
                                {t('revenue_cash_flow.new_sale')}
                            </span>
                        </div>
                        {showComparison && (
                            <div className="flex items-center gap-1.5">
                                <svg width="20" height="10">
                                    <line
                                        x1="0"
                                        y1="5"
                                        x2="20"
                                        y2="5"
                                        stroke={CHART_COLORS.INDIGO_PURPLE}
                                        strokeWidth="2"
                                        strokeDasharray="4 2"
                                        opacity="0.5"
                                    />
                                </svg>
                                <span className="text-xs text-purple-400">
                                    {t('revenue_cash_flow.new_sale')}{' '}
                                    {dayjs(filters.gte)
                                        .subtract(1, 'year')
                                        .format('YYYY')}
                                </span>
                            </div>
                        )}
                        {/* Upsell */}
                        <div className="flex items-center gap-1.5">
                            <svg width="20" height="10">
                                <line
                                    x1="0"
                                    y1="5"
                                    x2="20"
                                    y2="5"
                                    stroke={CHART_COLORS.AMBER_ORANGE}
                                    strokeWidth="2"
                                />
                            </svg>
                            <span className="text-xs font-medium text-amber-600">
                                {t('revenue_cash_flow.upsell')}
                            </span>
                        </div>
                        {showComparison && (
                            <div className="flex items-center gap-1.5">
                                <svg width="20" height="10">
                                    <line
                                        x1="0"
                                        y1="5"
                                        x2="20"
                                        y2="5"
                                        stroke={CHART_COLORS.AMBER_ORANGE}
                                        strokeWidth="2"
                                        strokeDasharray="4 2"
                                        opacity="0.5"
                                    />
                                </svg>
                                <span className="text-xs text-amber-400">
                                    {t('revenue_cash_flow.upsell')}{' '}
                                    {dayjs(filters.gte)
                                        .subtract(1, 'year')
                                        .format('YYYY')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </Paper>

            {/* Stacked Bar Chart */}
            <Paper className="space-y-3">
                <Typogaphy size="medium" className="font-medium">
                    {t('revenue_cash_flow.revenue_breakdown_by_type')}
                </Typogaphy>

                <ResponsiveContainer
                    width="100%"
                    height={showComparison ? 350 : 300}
                >
                    <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={SCHEMES.gray}
                        />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12 }}
                            width={60}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        {/* Current year bars */}
                        <Bar
                            dataKey="renewal"
                            name={t('revenue_cash_flow.renewal')}
                            stackId="current"
                            fill={CHART_COLORS.EMERALD_GREEN}
                        />
                        <Bar
                            dataKey="newSale"
                            name={t('revenue_cash_flow.new_sale')}
                            stackId="current"
                            fill={CHART_COLORS.INDIGO_PURPLE}
                        />
                        <Bar
                            dataKey="upsell"
                            name={t('revenue_cash_flow.upsell')}
                            stackId="current"
                            fill={CHART_COLORS.AMBER_ORANGE}
                        />
                        {/* Previous year bars (when comparison enabled) */}
                        {showComparison && (
                            <>
                                <Bar
                                    dataKey="prevRenewal"
                                    name={`${t('revenue_cash_flow.renewal')} (${t('common.last_year')})`}
                                    stackId="previous"
                                    fill={CHART_COLORS.EMERALD_GREEN}
                                    opacity={0.4}
                                />
                                <Bar
                                    dataKey="prevNewSale"
                                    name={`${t('revenue_cash_flow.new_sale')} (${t('common.last_year')})`}
                                    stackId="previous"
                                    fill={CHART_COLORS.INDIGO_PURPLE}
                                    opacity={0.4}
                                />
                                <Bar
                                    dataKey="prevUpsell"
                                    name={`${t('revenue_cash_flow.upsell')} (${t('common.last_year')})`}
                                    stackId="previous"
                                    fill={CHART_COLORS.AMBER_ORANGE}
                                    opacity={0.4}
                                />
                            </>
                        )}
                    </BarChart>
                </ResponsiveContainer>
                {/* Custom Legend */}
                <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-2">
                    {/* Renewal */}
                    <div className="flex items-center gap-1.5">
                        <span
                            className="w-3 h-3 rounded-sm"
                            style={{
                                backgroundColor: CHART_COLORS.EMERALD_GREEN,
                            }}
                        />
                        <span className="text-xs font-medium text-green-600">
                            {t('revenue_cash_flow.renewal')}
                        </span>
                    </div>
                    {showComparison && (
                        <div className="flex items-center gap-1.5">
                            <span
                                className="w-3 h-3 rounded-sm opacity-40"
                                style={{
                                    backgroundColor: CHART_COLORS.EMERALD_GREEN,
                                }}
                            />
                            <span className="text-xs text-green-400">
                                {t('revenue_cash_flow.renewal')}{' '}
                                {dayjs(filters.gte)
                                    .subtract(1, 'year')
                                    .format('YYYY')}
                            </span>
                        </div>
                    )}
                    {/* New Sale */}
                    <div className="flex items-center gap-1.5">
                        <span
                            className="w-3 h-3 rounded-sm"
                            style={{
                                backgroundColor: CHART_COLORS.INDIGO_PURPLE,
                            }}
                        />
                        <span className="text-xs font-medium text-purple-600">
                            {t('revenue_cash_flow.new_sale')}
                        </span>
                    </div>
                    {showComparison && (
                        <div className="flex items-center gap-1.5">
                            <span
                                className="w-3 h-3 rounded-sm opacity-40"
                                style={{
                                    backgroundColor: CHART_COLORS.INDIGO_PURPLE,
                                }}
                            />
                            <span className="text-xs text-purple-400">
                                {t('revenue_cash_flow.new_sale')}{' '}
                                {dayjs(filters.gte)
                                    .subtract(1, 'year')
                                    .format('YYYY')}
                            </span>
                        </div>
                    )}
                    {/* Upsell */}
                    <div className="flex items-center gap-1.5">
                        <span
                            className="w-3 h-3 rounded-sm"
                            style={{
                                backgroundColor: CHART_COLORS.AMBER_ORANGE,
                            }}
                        />
                        <span className="text-xs font-medium text-amber-600">
                            {t('revenue_cash_flow.upsell')}
                        </span>
                    </div>
                    {showComparison && (
                        <div className="flex items-center gap-1.5">
                            <span
                                className="w-3 h-3 rounded-sm opacity-40"
                                style={{
                                    backgroundColor: CHART_COLORS.AMBER_ORANGE,
                                }}
                            />
                            <span className="text-xs text-amber-400">
                                {t('revenue_cash_flow.upsell')}{' '}
                                {dayjs(filters.gte)
                                    .subtract(1, 'year')
                                    .format('YYYY')}
                            </span>
                        </div>
                    )}
                </div>
            </Paper>

            {/* Detail Table */}
            {chartData.length > 0 && (
                <Paper className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Typogaphy size="medium" className="font-medium">
                            {t('revenue_cash_flow.monthly_detail')}
                        </Typogaphy>
                        <ExcelExportButton
                            data={exportData}
                            filename="revenue-cash-flow"
                        />
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-600">
                                        {t('common.month')}
                                    </th>
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-blue-600">
                                        {t('revenue_cash_flow.total_revenue')}
                                    </th>
                                    {showComparison && (
                                        <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-blue-400">
                                            YoY %
                                        </th>
                                    )}
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-green-600">
                                        {t('revenue_cash_flow.renewal')}
                                    </th>
                                    {showComparison && (
                                        <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-green-400">
                                            YoY %
                                        </th>
                                    )}
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-purple-600">
                                        {t('revenue_cash_flow.new_sale')}
                                    </th>
                                    {showComparison && (
                                        <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-purple-400">
                                            YoY %
                                        </th>
                                    )}
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-amber-600">
                                        {t('revenue_cash_flow.upsell')}
                                    </th>
                                    {showComparison && (
                                        <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-amber-400">
                                            YoY %
                                        </th>
                                    )}
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-600">
                                        {t('revenue_cash_flow.invoices')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {chartData.map((row, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="border border-gray-200 px-3 py-2 font-medium">
                                            {row.month}
                                        </td>
                                        <td className="border border-gray-200 px-3 py-2 text-right font-bold text-blue-600">
                                            {formatCurrency(row.totalRevenue)}
                                        </td>
                                        {showComparison && (
                                            <td className="border border-gray-200 px-3 py-2">
                                                {renderGrowthCell(
                                                    row.totalGrowth
                                                )}
                                            </td>
                                        )}
                                        <td className="border border-gray-200 px-3 py-2 text-right text-green-600">
                                            {formatCurrency(row.renewal)}
                                        </td>
                                        {showComparison && (
                                            <td className="border border-gray-200 px-3 py-2">
                                                {renderGrowthCell(
                                                    row.renewalGrowth
                                                )}
                                            </td>
                                        )}
                                        <td className="border border-gray-200 px-3 py-2 text-right text-purple-600">
                                            {formatCurrency(row.newSale)}
                                        </td>
                                        {showComparison && (
                                            <td className="border border-gray-200 px-3 py-2">
                                                {renderGrowthCell(
                                                    row.newSaleGrowth
                                                )}
                                            </td>
                                        )}
                                        <td className="border border-gray-200 px-3 py-2 text-right text-amber-600">
                                            {formatCurrency(row.upsell)}
                                        </td>
                                        {showComparison && (
                                            <td className="border border-gray-200 px-3 py-2">
                                                {renderGrowthCell(
                                                    row.upsellGrowth
                                                )}
                                            </td>
                                        )}
                                        <td className="border border-gray-200 px-3 py-2 text-right">
                                            {row.count}
                                        </td>
                                    </tr>
                                ))}
                                {/* Total row */}
                                <tr className="bg-gray-100 font-bold">
                                    <td className="border border-gray-200 px-3 py-2">
                                        {t('common.total')}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right text-blue-700">
                                        {formatCurrency(summary.totalRevenue)}
                                    </td>
                                    {showComparison && (
                                        <td className="border border-gray-200 px-3 py-2">
                                            {renderGrowthCell(
                                                calculateGrowthRate(
                                                    summary.totalRevenue,
                                                    chartData.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.prevTotalRevenue,
                                                        0
                                                    )
                                                )
                                            )}
                                        </td>
                                    )}
                                    <td className="border border-gray-200 px-3 py-2 text-right text-green-700">
                                        {formatCurrency(summary.renewalRevenue)}
                                    </td>
                                    {showComparison && (
                                        <td className="border border-gray-200 px-3 py-2">
                                            {renderGrowthCell(
                                                calculateGrowthRate(
                                                    summary.renewalRevenue,
                                                    chartData.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.prevRenewal,
                                                        0
                                                    )
                                                )
                                            )}
                                        </td>
                                    )}
                                    <td className="border border-gray-200 px-3 py-2 text-right text-purple-700">
                                        {formatCurrency(summary.newSaleRevenue)}
                                    </td>
                                    {showComparison && (
                                        <td className="border border-gray-200 px-3 py-2">
                                            {renderGrowthCell(
                                                calculateGrowthRate(
                                                    summary.newSaleRevenue,
                                                    chartData.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.prevNewSale,
                                                        0
                                                    )
                                                )
                                            )}
                                        </td>
                                    )}
                                    <td className="border border-gray-200 px-3 py-2 text-right text-amber-700">
                                        {formatCurrency(summary.upsellRevenue)}
                                    </td>
                                    {showComparison && (
                                        <td className="border border-gray-200 px-3 py-2">
                                            {renderGrowthCell(
                                                calculateGrowthRate(
                                                    summary.upsellRevenue,
                                                    chartData.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.prevUpsell,
                                                        0
                                                    )
                                                )
                                            )}
                                        </td>
                                    )}
                                    <td className="border border-gray-200 px-3 py-2 text-right">
                                        {summary.totalAllocations}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Paper>
            )}

            {/* Employee Breakdown Table */}
            {employeeBreakdownData.length > 0 && uniqueMonths.length > 0 && (
                <Paper className="space-y-3">
                    <div className="flex items-center justify-between">
                        <Typogaphy size="medium" className="font-medium">
                            {t('revenue_cash_flow.employee_monthly_breakdown')}
                        </Typogaphy>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse min-w-max">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-[150px]">
                                        {t('common.employee')}
                                    </th>
                                    <th className="border border-gray-200 px-3 py-2 text-right font-semibold text-blue-600 min-w-[120px]">
                                        {t('common.total')}
                                    </th>
                                    {uniqueMonths.map((month) => (
                                        <th
                                            key={month.key}
                                            className="border border-gray-200 px-3 py-2 text-right font-semibold text-gray-600 min-w-[100px]"
                                        >
                                            {month.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedEmployeeData.map(
                                    (employee, index) => (
                                        <tr
                                            key={employee.employee_id}
                                            className={
                                                index % 2 === 0
                                                    ? 'bg-white'
                                                    : 'bg-gray-50'
                                            }
                                        >
                                            <td className="border border-gray-200 px-3 py-2 font-medium sticky left-0 bg-inherit z-10">
                                                <div className="flex flex-col">
                                                    <span>
                                                        {employee.employee_name}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {
                                                            employee.allocation_count
                                                        }{' '}
                                                        {t(
                                                            'revenue_cash_flow.invoices'
                                                        )}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="border border-gray-200 px-3 py-2 text-right font-bold text-blue-600">
                                                {formatCurrency(
                                                    employee.total_revenue
                                                )}
                                            </td>
                                            {uniqueMonths.map((month) => {
                                                const monthData =
                                                    employee.monthlyData.get(
                                                        month.key
                                                    )
                                                return (
                                                    <td
                                                        key={month.key}
                                                        className="border border-gray-200 px-3 py-2 text-right"
                                                    >
                                                        {monthData ? (
                                                            <div className="flex flex-col">
                                                                <span className="font-medium">
                                                                    {formatCurrency(
                                                                        monthData.total
                                                                    )}
                                                                </span>
                                                                <div className="flex justify-end gap-1 text-[10px]">
                                                                    <span className="text-green-600">
                                                                        R:
                                                                        {(
                                                                            monthData.renewal /
                                                                            1_000_000
                                                                        ).toFixed(
                                                                            1
                                                                        )}
                                                                        M
                                                                    </span>
                                                                    <span className="text-purple-600">
                                                                        N:
                                                                        {(
                                                                            monthData.newSale /
                                                                            1_000_000
                                                                        ).toFixed(
                                                                            1
                                                                        )}
                                                                        M
                                                                    </span>
                                                                    <span className="text-amber-600">
                                                                        U:
                                                                        {(
                                                                            monthData.upsell /
                                                                            1_000_000
                                                                        ).toFixed(
                                                                            1
                                                                        )}
                                                                        M
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-300">
                                                                -
                                                            </span>
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    )
                                )}
                                {/* Total row */}
                                <tr className="bg-gray-100 font-bold">
                                    <td className="border border-gray-200 px-3 py-2 sticky left-0 bg-gray-100 z-10">
                                        {t('common.total')}
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-right text-blue-700">
                                        {formatCurrency(summary.totalRevenue)}
                                    </td>
                                    {uniqueMonths.map((month) => {
                                        const monthTotal = chartData.find(
                                            (d) => d.rawMonth === month.key
                                        )
                                        return (
                                            <td
                                                key={month.key}
                                                className="border border-gray-200 px-3 py-2 text-right"
                                            >
                                                {monthTotal ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">
                                                            {formatCurrency(
                                                                monthTotal.totalRevenue
                                                            )}
                                                        </span>
                                                        <div className="flex justify-end gap-1 text-[10px]">
                                                            <span className="text-green-700">
                                                                R:
                                                                {(
                                                                    monthTotal.renewal /
                                                                    1_000_000
                                                                ).toFixed(1)}
                                                                M
                                                            </span>
                                                            <span className="text-purple-700">
                                                                N:
                                                                {(
                                                                    monthTotal.newSale /
                                                                    1_000_000
                                                                ).toFixed(1)}
                                                                M
                                                            </span>
                                                            <span className="text-amber-700">
                                                                U:
                                                                {(
                                                                    monthTotal.upsell /
                                                                    1_000_000
                                                                ).toFixed(1)}
                                                                M
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Legend and Pagination */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-2">
                        {/* Legend for abbreviations */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>
                                <span className="text-green-600 font-medium">
                                    R
                                </span>{' '}
                                = {t('revenue_cash_flow.renewal')}
                            </span>
                            <span>
                                <span className="text-purple-600 font-medium">
                                    N
                                </span>{' '}
                                = {t('revenue_cash_flow.new_sale')}
                            </span>
                            <span>
                                <span className="text-amber-600 font-medium">
                                    U
                                </span>{' '}
                                = {t('revenue_cash_flow.upsell')}
                            </span>
                        </div>

                        {/* Pagination */}
                        {employeeBreakdownData.length > employeePageSize && (
                            <div className="flex items-center gap-3">
                                {/* Page size selector */}
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <span>{t('common.show')}</span>
                                    <select
                                        value={employeePageSize}
                                        onChange={(e) => {
                                            setEmployeePageSize(
                                                Number(e.target.value)
                                            )
                                            setEmployeePage(1)
                                        }}
                                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    <span>
                                        / {employeeBreakdownData.length}
                                    </span>
                                </div>

                                {/* Page navigation */}
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() =>
                                            setEmployeePage((p) =>
                                                Math.max(1, p - 1)
                                            )
                                        }
                                        disabled={employeePage === 1}
                                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <span className="text-sm text-gray-600 min-w-[80px] text-center">
                                        {employeePage} / {totalEmployeePages}
                                    </span>
                                    <button
                                        onClick={() =>
                                            setEmployeePage((p) =>
                                                Math.min(
                                                    totalEmployeePages,
                                                    p + 1
                                                )
                                            )
                                        }
                                        disabled={
                                            employeePage === totalEmployeePages
                                        }
                                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </Paper>
            )}

            {chartData.length === 0 && !isLoading && (
                <Paper className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                    <Banknote size={48} className="text-gray-300 mb-4" />
                    <Typogaphy>{t('no_data_available')}</Typogaphy>
                </Paper>
            )}
        </div>
    )
}

export default RevenueCashFlow
